/**
 * Exchange Network Status API Configurations
 * Direct API endpoints for fetching network status from exchanges
 */

import { EXCHANGE_INDICES } from '../types/networkStatus';

export interface ExchangeApiConfig {
  url: string | (() => string);
  method: 'GET' | 'POST';
  headers?: Record<string, string>;
  corsAllowed: boolean;
  requiresAuth?: boolean;
  parseResponse: (data: any) => any;
}

/**
 * Exchange API configurations
 * Based on docs/network-status-endpoints.md
 */
export const EXCHANGE_API_CONFIGS: Record<number, ExchangeApiConfig> = {
  [EXCHANGE_INDICES.Binance]: {
    url: 'https://www.binance.com/bapi/capital/v2/public/capital/getNetworkCoinAll',
    method: 'GET',
    headers: {
      'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
    },
    corsAllowed: true,
    parseResponse: (data) => parseBinanceResponse(data),
  },
  [EXCHANGE_INDICES.Upbit]: {
    url: 'https://ccx.upbit.com/api/v1/status/network/wallet',
    method: 'GET',
    corsAllowed: true,
    parseResponse: async (data) => {
      // Fetch withdrawal fees separately
      try {
        const feeResponse = await fetch('https://ccx.upbit.com/api/v1/status/withdraw_fee');
        const feeData = await feeResponse.json();
        return parseUpbitResponseWithFees(data, feeData);
      } catch (error) {
        console.warn('Failed to fetch Upbit withdrawal fees, using basic data:', error);
        return parseUpbitResponse(data);
      }
    },
  },
  [EXCHANGE_INDICES.MEXC]: {
    url: 'https://api.mexc.com/api/v3/capital/config/getall',
    method: 'GET',
    corsAllowed: true, // Test if it works
    parseResponse: (data) => parseMexcResponse(data),
  },
  [EXCHANGE_INDICES.GateIO]: {
    url: 'https://api.gateio.ws/api/v4/spot/currencies',
    method: 'GET',
    corsAllowed: true, // Test if it works
    parseResponse: (data) => parseGateioResponse(data),
  },
  [EXCHANGE_INDICES.Coinone]: {
    url: 'https://api.coinone.co.kr/public/v2/currencies',
    method: 'GET',
    corsAllowed: true, // Test if it works
    parseResponse: (data) => parseCoinoneResponse(data),
  },
  [EXCHANGE_INDICES.Bithumb]: {
    url: () => `https://gw.bithumb.com/exchange/v1/coin-inout/info?_=${Date.now()}&retry=0`,
    method: 'GET',
    headers: {
      'Origin': 'https://www.bithumb.com',
      'Referer': 'https://www.bithumb.com/',
    },
    corsAllowed: true, // Test if it works
    parseResponse: (data) => parseBitthumbResponse(data),
  },
  [EXCHANGE_INDICES.OKX]: {
    url: () => `https://www.okx.com/v2/asset/currency/status?page=1&t=${Date.now()}`,
    method: 'GET',
    corsAllowed: true, // Test if it works
    parseResponse: (data) => parseOkxResponse(data),
  },
  [EXCHANGE_INDICES.Bybit]: {
    url: 'https://www.bybit.com/x-api/v3/private/cht/asset-common/coin-status',
    method: 'GET',
    headers: {
      'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
    },
    corsAllowed: true, // Test if it works
    parseResponse: (data) => parseBybitResponse(data),
  },
  [EXCHANGE_INDICES.Bitget]: {
    url: 'https://api.bitget.com/api/v2/spot/public/coins',
    method: 'GET',
    corsAllowed: true,
    parseResponse: (data) => parseBitgetResponse(data),
  },
  [EXCHANGE_INDICES.KuCoin]: {
    url: 'https://api.kucoin.com/api/v1/currencies',
    method: 'GET',
    corsAllowed: true,
    parseResponse: (data) => parseKucoinResponse(data),
  },
  [EXCHANGE_INDICES.HTX]: {
    url: 'https://api.huobi.pro/v2/reference/currencies',
    method: 'GET',
    corsAllowed: true, // Works from browser
    parseResponse: (data) => parseHtxResponse(data),
  },
};

// Parser functions for each exchange

function parseBinanceResponse(response: any) {
  // Binance wraps data in a response object
  const data = response.data || response;
  if (!Array.isArray(data)) return [];
  
  return data.map((coin: any) => ({
    symbol: coin.coin,
    name: coin.name || coin.coin,
    networks: (coin.networkList || []).map((network: any) => ({
      network: network.network,
      deposit_enabled: network.depositEnable === true,
      withdraw_enabled: network.withdrawEnable === true,
      min_deposit: parseFloat(network.depositMin) || undefined,
      min_withdraw: parseFloat(network.withdrawMin) || undefined,
      withdraw_fee: parseFloat(network.withdrawFee) || undefined,
    })),
  }));
}

function parseUpbitResponse(data: any) {
  if (!data || !Array.isArray(data)) return [];
  
  // Group by currency
  const grouped: Record<string, any> = {};
  
  data.forEach((item: any) => {
    const currency = item.currency;
    if (!grouped[currency]) {
      grouped[currency] = {
        symbol: currency,
        name: currency,
        networks: [],
      };
    }
    
    // Use net_type for network name (ETH, TRX, etc.) or fallback to network_name
    const networkName = item.net_type || item.network_name || currency;
    
    grouped[currency].networks.push({
      network: networkName,
      deposit_enabled: item.wallet_state === 'working',
      withdraw_enabled: item.wallet_state === 'working',
    });
  });
  
  return Object.values(grouped);
}

function parseUpbitResponseWithFees(data: any, feeData: any) {
  if (!data || !Array.isArray(data)) return [];
  
  // Create a fee lookup map
  const feeMap = new Map<string, any>();
  if (feeData && feeData.withdraw_fee_conditions) {
    feeData.withdraw_fee_conditions.forEach((fee: any) => {
      const key = `${fee.currency}-${fee.net_type || fee.network_name || fee.currency}`;
      feeMap.set(key, {
        withdraw_fee: fee.withdraw_fee !== undefined ? parseFloat(fee.withdraw_fee) : undefined,
        min_withdraw: fee.minimum !== undefined ? parseFloat(fee.minimum) : undefined,
      });
    });
  }
  
  // Group by currency
  const grouped: Record<string, any> = {};
  
  data.forEach((item: any) => {
    const currency = item.currency;
    if (!grouped[currency]) {
      grouped[currency] = {
        symbol: currency,
        name: currency,
        networks: [],
      };
    }
    
    // Use net_type for network name (ETH, TRX, etc.) or fallback to network_name
    const networkName = item.net_type || item.network_name || currency;
    
    // Look up fees
    const feeKey = `${currency}-${networkName}`;
    const fees = feeMap.get(feeKey) || {};
    
    grouped[currency].networks.push({
      network: networkName,
      deposit_enabled: item.wallet_state === 'working',
      withdraw_enabled: item.wallet_state === 'working',
      withdraw_fee: fees.withdraw_fee,
      min_withdraw: fees.min_withdraw,
    });
  });
  
  return Object.values(grouped);
}

function parseMexcResponse(data: any) {
  // Similar to Binance format
  return parseBinanceResponse(data);
}

function parseGateioResponse(data: any) {
  if (!Array.isArray(data)) return [];
  
  return data.map((currency: any) => ({
    symbol: currency.currency,
    name: currency.currency,
    networks: [{
      network: currency.chain || currency.currency,
      deposit_enabled: currency.deposit !== false,
      withdraw_enabled: currency.withdraw !== false,
    }],
  }));
}

function parseCoinoneResponse(data: any) {
  if (!data || !data.currencies) return [];
  
  return data.currencies.map((currency: any) => ({
    symbol: currency.currency,
    name: currency.name || currency.currency,
    networks: [{
      network: currency.currency,
      deposit_enabled: currency.deposit_status === 'normal',
      withdraw_enabled: currency.withdraw_status === 'normal',
    }],
  }));
}

function parseBitthumbResponse(data: any) {
  if (!data || !data.data) return [];
  
  // Create a map of coin names to symbols for network name lookup
  const coinNameToSymbol = new Map<string, string>();
  data.data.forEach((coin: any) => {
    if (coin.coinNameEn) {
      coinNameToSymbol.set(coin.coinNameEn.toLowerCase(), coin.coinSymbol);
    }
  });
  
  // Bithumb returns an array of coin objects
  return data.data.map((coin: any) => ({
    symbol: coin.coinSymbol,
    name: coin.coinNameEn || coin.coinName || coin.coinSymbol,
    networks: (coin.networkInfoList || []).map((network: any) => {
      // Try to find a matching coin symbol for the network name
      const networkNameLower = network.networkName.toLowerCase();
      const matchingSymbol = coinNameToSymbol.get(networkNameLower);
      
      return {
        network: matchingSymbol || network.networkName, // Use symbol if found, otherwise use network name
        deposit_enabled: network.isDepositAvailable === true,
        withdraw_enabled: network.isWithdrawAvailable === true,
        withdraw_fee: parseFloat(network.withdrawFeeQuantity) || undefined,
        min_withdraw: parseFloat(network.withdrawMinimumQuantity) || undefined,
        min_deposit: parseFloat(network.smallDepositBaseQuantity) || undefined,
      };
    }),
  }));
}

function parseOkxResponse(data: any) {
  if (!data || !data.data) return [];
  
  // OKX returns paginated data
  return data.data.map((item: any) => ({
    symbol: item.currency,
    name: item.currency,
    networks: [{
      network: item.chain || item.currency,
      deposit_enabled: item.deposit === '1',
      withdraw_enabled: item.withdraw === '1',
    }],
  }));
}

function parseBybitResponse(data: any) {
  if (!data || !data.result || !data.result.coins) return [];
  
  return data.result.coins.map((coin: any) => ({
    symbol: coin.coin,
    name: coin.coinFullName || coin.coin,
    networks: (coin.coinChainStatusItem || []).map((chain: any) => ({
      network: chain.chain,
      // Use network-specific deposit/withdraw status
      deposit_enabled: chain.depositStatus === 1,
      withdraw_enabled: chain.withdrawStatus === 1,
      withdraw_fee: parseFloat(chain.withdrawFee) || undefined,
      min_withdraw: parseFloat(chain.withdrawMin) || undefined,
      min_deposit: parseFloat(chain.minDepositAmount) || undefined,
    })),
  }));
}

function parseBitgetResponse(data: any) {
  if (!data || !data.data) return [];
  
  return data.data.map((coin: any) => ({
    symbol: coin.coin,
    name: coin.coin,
    networks: (coin.chains || []).map((chain: any) => ({
      network: chain.chain,
      // Bitget uses string "true"/"false" for these fields
      deposit_enabled: chain.rechargeable === 'true',
      withdraw_enabled: chain.withdrawable === 'true',
      min_deposit: parseFloat(chain.minDepositAmount) || undefined,
      min_withdraw: parseFloat(chain.minWithdrawAmount) || undefined,
      withdraw_fee: parseFloat(chain.withdrawFee) || undefined,
    })),
  }));
}

function parseKucoinResponse(data: any) {
  if (!data || !data.data) return [];
  
  // KuCoin doesn't have chains property - it's a flat structure
  // Each currency is essentially one network
  return data.data.map((currency: any) => ({
    symbol: currency.currency,
    name: currency.fullName || currency.currency,
    networks: [{
      network: currency.currency, // Use currency as network name
      deposit_enabled: currency.isDepositEnabled === true,
      withdraw_enabled: currency.isWithdrawEnabled === true,
      min_deposit: parseFloat(currency.depositMinSize) || undefined,
      min_withdraw: parseFloat(currency.withdrawalMinSize) || undefined,
      withdraw_fee: parseFloat(currency.withdrawalMinFee) || undefined,
    }],
  }));
}

function parseHtxResponse(data: any) {
  if (!data || !data.data) return [];
  
  // HTX v2 API returns currencies with lowercase symbols
  // Filter out currencies that are not in "normal" status (e.g., delisted)
  return data.data
    .filter((item: any) => item.instStatus === 'normal')
    .map((item: any) => ({
      symbol: item.currency.toUpperCase(), // Convert to uppercase for consistency
      name: item.currency.toUpperCase(),
      networks: (item.chains || []).map((chain: any) => ({
        network: chain.displayName || chain.chain, // Use displayName if available, otherwise chain
        deposit_enabled: chain.depositStatus === 'allowed',
        withdraw_enabled: chain.withdrawStatus === 'allowed',
        withdraw_fee: parseFloat(chain.transactFeeWithdraw) || undefined,
        min_withdraw: parseFloat(chain.minWithdrawAmt) || undefined,
        min_deposit: parseFloat(chain.minDepositAmt) || undefined,
      })),
    }));
}